import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Mail, Phone } from "lucide-react";
import SectionLabel from "../components/SectionLabel";

const REQUIREMENTS = [
  "Exhibition / Stall",
  "Retail / RCIP",
  "Website / App",
  "Content / Video",
  "Branding",
  "Event",
];

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

// ---------- Input hardening ----------
// Strict RFC-5322-compatible email validator (defence-in-depth on top of HTML5)
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/;
// Phone: digits, spaces, +, -, (, ), max 20 chars
const PHONE_RE = /^[+\d][\d\s\-()]{5,19}$/;

// Strip control chars + CR/LF (prevents email header injection via Name/Subject)
const sanitizeOneLine = (s) =>
  String(s ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, 200);

// Multi-line: allow newlines but strip control chars; cap length
const sanitizeMessage = (s) =>
  String(s ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, "")
    .trim()
    .slice(0, 4000);

const FIELD_LIMITS = {
  name: 80,
  company: 120,
  phone: 20,
  email: 120,
  message: 4000,
};

const Field = ({ label, children, testid }) => (
  <label className="block group" data-testid={`field-${testid}`}>
    <div className="mono-label mb-3 group-focus-within:text-[#AE519F] transition-colors">
      {label}
    </div>
    {children}
  </label>
);

const inputBase =
  "w-full bg-transparent border-b border-black/40 text-black py-3 text-base focus:border-[#AE519F] focus:outline-none transition-colors placeholder:text-black/40";

const Connect = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    requirement: "Branding",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) => {
    const { name, value } = e.target;
    const limit = FIELD_LIMITS[name];
    setForm((f) => ({ ...f, [name]: limit ? value.slice(0, limit) : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation + hardening (defence in depth - Web3Forms also validates)
    const cleanName = sanitizeOneLine(form.name);
    const cleanCompany = sanitizeOneLine(form.company);
    const cleanPhone = sanitizeOneLine(form.phone);
    const cleanEmail = sanitizeOneLine(form.email).toLowerCase();
    const cleanRequirement = sanitizeOneLine(form.requirement);
    const cleanMessage = sanitizeMessage(form.message);

    if (!cleanName || cleanName.length < 2) {
      setStatus({ state: "error", msg: "Please enter your name." });
      return;
    }
    if (!EMAIL_RE.test(cleanEmail)) {
      setStatus({ state: "error", msg: "Please enter a valid email address." });
      return;
    }
    if (!PHONE_RE.test(cleanPhone)) {
      setStatus({ state: "error", msg: "Please enter a valid phone number." });
      return;
    }
    if (!cleanMessage || cleanMessage.length < 10) {
      setStatus({
        state: "error",
        msg: "Please share a little more about your requirement (10+ characters).",
      });
      return;
    }
    if (!REQUIREMENTS.includes(cleanRequirement)) {
      setStatus({ state: "error", msg: "Please pick a requirement." });
      return;
    }

    setStatus({ state: "loading", msg: "" });

    // Submit as application/x-www-form-urlencoded — the same encoding Web3Forms'
    // own HTML-form example produces. Avoids the CORS preflight that JSON
    // triggers and avoids any multipart-parser edge cases.
    const params = new URLSearchParams();
    params.append("access_key", WEB3FORMS_KEY);
    params.append("from_name", "UPRCSE Website Enquiry");
    params.append(
      "subject",
      `New enquiry - ${cleanRequirement} - ${cleanName}`,
    );
    params.append("name", cleanName);
    params.append("company", cleanCompany);
    params.append("phone", cleanPhone);
    params.append("email", cleanEmail);
    params.append("requirement", cleanRequirement);
    params.append("message", cleanMessage);
    // Honeypot field: real users leave this empty; bots fill it.
    params.append("botcheck", "");

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: params.toString(),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({
          state: "success",
          msg: "Enquiry received. We'll be in touch shortly.",
        });
        setForm({
          name: "",
          company: "",
          phone: "",
          email: "",
          requirement: "Branding",
          message: "",
        });
      } else {
        setStatus({
          state: "error",
          msg: data.message || "Something went wrong. Please try again.",
        });
      }
    } catch (err) {
      setStatus({
        state: "error",
        msg: "Network error. Please try again or email info@uprcse.com.",
      });
    }
  };

  return (
    <div data-testid="page-connect" className="pt-32 md:pt-40">
      {/* Header */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <SectionLabel className="mb-8" testid="label-connect">
          [ CONNECT ] - START THE CONVERSATION
        </SectionLabel>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: [0.22, 0.61, 0.36, 1] }}
          className="font-archivo font-black text-black h-tighter text-5xl md:text-6xl lg:text-7xl max-w-5xl"
          style={{ fontFamily: "Archivo, sans-serif" }}
        >
          Whether you're building from the
          <span className="text-[#AE519F]"> ground up </span>
          or restructuring an existing brand - we work end-to-end.
        </motion.h1>
      </section>

      {/* Two-column: Studio + Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Studio specs */}
          <div className="lg:col-span-5">
            <div className="border border-black/30 p-7 md:p-8 bg-[#CCD6DC] relative">
              <div className="absolute top-0 left-0 w-3 h-3 border-l border-t border-black" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r border-t border-black" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l border-b border-black" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r border-b border-black" />

              <div className="mono-label mb-6">STUDIO / MUMBAI</div>

              <div className="space-y-7">
                <div className="flex items-start gap-4">
                  <MapPin
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#AE519F] mt-1 flex-shrink-0"
                  />
                  <div>
                    <div className="mono-label mb-1">ADDRESS</div>
                    <address
                      className="not-italic text-black text-base leading-relaxed"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                      data-testid="contact-address"
                    >
                      UPRCSE
                      <br />
                      1104 Bluebell, Hubtown Gardenia,
                      <br />
                      Gaurav Enclave Road, Mira Road,
                      <br />
                      Mumbai 401 107.
                    </address>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#AE519F] mt-1 flex-shrink-0"
                  />
                  <div>
                    <div className="mono-label mb-1">EMAIL</div>
                    <a
                      href="mailto:info@uprcse.com"
                      data-testid="contact-email-link"
                      className="text-black hover:text-[#AE519F] transition-colors"
                      style={{
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: "14px",
                      }}
                    >
                      info@uprcse.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#AE519F] mt-1 flex-shrink-0"
                  />
                  <div>
                    <div className="mono-label mb-1">MOBILE</div>
                    <a
                      href="tel:+919892510233"
                      data-testid="contact-phone-link"
                      className="text-black hover:text-[#AE519F] transition-colors"
                      style={{
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: "14px",
                      }}
                    >
                      +91 98925 10233
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-black/30">
                <div className="mono-label mb-3">CAPABILITIES</div>
                <p
                  className="text-black/75 text-sm leading-relaxed"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  Branding · Identity · Websites · Apps · Exhibitions · Retail
                  RCIP · Packaging · Events · Content & Video
                </p>
              </div>
            </div>
          </div>

          {/* Enquiry Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="enquiry-form"
              className="border border-black/30 p-7 md:p-9 bg-[#CCD6DC] relative"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="mono-label">ENQUIRY / FORM</div>
                <div className="mono-label text-[#AE519F]">RES. ≤ 24h</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
                <Field label="01 - Name" testid="name">
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    placeholder="Your full name"
                    className={inputBase}
                    data-testid="input-name"
                  />
                </Field>

                <Field label="02 - Company" testid="company">
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    placeholder="Organisation"
                    className={inputBase}
                    data-testid="input-company"
                  />
                </Field>

                <Field label="03 - Contact Number" testid="phone">
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    required
                    placeholder="+91 ..."
                    className={inputBase}
                    data-testid="input-phone"
                  />
                </Field>

                <Field label="04 - Email" testid="email">
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    placeholder="name@company.com"
                    className={inputBase}
                    data-testid="input-email"
                  />
                </Field>

                <div className="md:col-span-2">
                  <Field label="05 - Nature of Requirement" testid="requirement">
                    <div className="flex flex-wrap gap-2">
                      {REQUIREMENTS.map((r) => {
                        const active = form.requirement === r;
                        return (
                          <button
                            type="button"
                            key={r}
                            onClick={() => setForm((f) => ({ ...f, requirement: r }))}
                            data-testid={`requirement-${r.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            className={`px-4 py-2 border text-xs uppercase tracking-[0.18em] transition-colors ${
                              active
                                ? "border-black bg-[#AE519F] text-white"
                                : "border-black/40 text-black hover:border-[#AE519F] hover:text-[#AE519F]"
                            }`}
                            style={{ fontFamily: "JetBrains Mono, monospace" }}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <Field label="06 - Message" testid="message">
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      rows={5}
                      placeholder="Tell us about your brand, scope, and timing..."
                      className={`${inputBase} resize-none`}
                      data-testid="input-message"
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div className="mono-label text-black/60">
                  Submission is sent securely via Web3Forms.
                </div>
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid="submit-enquiry"
                  className="liquid-btn is-primary inline-flex items-center gap-3 disabled:opacity-60"
                >
                  {status.state === "loading" ? "Submitting..." : "Submit Enquiry"}
                  <ArrowUpRight size={16} strokeWidth={1.6} />
                </button>
              </div>

              {status.state === "success" && (
                <div
                  data-testid="form-success"
                  className="mt-6 border border-black bg-[#AE519F]/15 px-5 py-4"
                >
                  <div className="mono-label text-[#AE519F] mb-1">SYSTEM / OK</div>
                  <div
                    className="text-black"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {status.msg}
                  </div>
                </div>
              )}
              {status.state === "error" && (
                <div
                  data-testid="form-error"
                  className="mt-6 border border-black bg-red-500/15 px-5 py-4"
                >
                  <div className="mono-label text-red-700 mb-1">SYSTEM / ERROR</div>
                  <div
                    className="text-black"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {status.msg}
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Connect;
